// File: app/src/utils/time.js
//
// Duration helpers. calculateTotalDuration and calculateSmartRemaining are
// thin wrappers over the workout compiler so the rest filter / Tabata
// trailing-rest behaviour lives in exactly one place.

import { compileWorkoutPlan } from "./workoutCompiler";

// Converts a value with unit into seconds. Supports seconds and minutes (and hours defensively).
export const toSeconds = (value, unit) => {
  if (value == null) return 0;
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  if (!unit) return n;
  const u = String(unit).toLowerCase();
  switch (u) {
    case "s":
    case "sec":
    case "secs":
    case "second":
    case "seconds":
      return n;
    case "m":
    case "min":
    case "mins":
    case "minute":
    case "minutes":
      return n * 60;
    case "h":
    case "hr":
    case "hrs":
    case "hour":
    case "hours":
      return n * 3600;
    default:
      return n;
  }
};

// Total duration of a workout plan in seconds. Defaults to the adjacent-recovery
// rest filter (matches runtime playback); pass `skipLastRest: false` to include
// every rest.
//
// `profile` (with `training_data`) and `historyMap` are forwarded to the
// compiler so rep-based phases produce non-zero estimates. Both are optional.
export const calculateTotalDuration = (
  plan,
  { skipLastRest = true, profile, historyMap } = {},
) => {
  if (!plan) return 0;
  const { totalDuration } = compileWorkoutPlan(plan, {
    skipLastRest,
    profile,
    historyMap,
  });
  return totalDuration;
};

/**
 * Estimate remaining workout seconds from the current position forward.
 *
 * Compiles the plan into the same queue the engine uses, locates the active
 * item by `currentPath` + `loopCounters`, and sums `durationSec` from the
 * next item onward. Rep phases now carry a real `durationSec` from the
 * compiler (estimator output), so no separate map is needed.
 *
 * @param {Object} plan                  Full workout plan tree
 * @param {Array}  currentPath           Lodash-style path to active leaf
 * @param {Object} loopCounters          { groupId: currentIteration (0-based) }
 * @param {number} currentPhaseRemaining Seconds left in the active phase
 * @param {Object} [opts]
 * @param {Object} [opts.profile]        User profile (training_data) for rep estimates
 * @param {Object} [opts.historyMap]     Exercise history map for blend
 * @param {Object} [opts.avgRepMap]      Deprecated. Per-nodeId override; kept for back-compat
 * @returns {number} Estimated remaining seconds
 */
export const calculateSmartRemaining = (
  plan,
  currentPath,
  loopCounters,
  currentPhaseRemaining,
  opts = {},
) => {
  if (!plan || !currentPath || currentPath.length === 0) return 0;
  const { profile, historyMap, avgRepMap } = opts;
  const { queue } = compileWorkoutPlan(plan, {
    skipLastRest: true,
    profile,
    historyMap,
  });
  if (queue.length === 0) return 0;

  const currentPathKey = currentPath.join(",");
  const loopKey = JSON.stringify(loopCounters || {});
  const idx = queue.findIndex(
    (item) =>
      item.sourceTreePath.join(",") === currentPathKey &&
      JSON.stringify(item.loopCounters) === loopKey,
  );
  if (idx < 0) return 0;

  let total = Math.max(0, currentPhaseRemaining || 0);
  for (let i = idx + 1; i < queue.length; i++) {
    const item = queue[i];
    if (item.isTimed) {
      total += item.durationSec;
      continue;
    }
    // Rep phase: prefer the legacy avgRepMap override if the caller still
    // supplies one (per-nodeId averages historically computed from this
    // session's phase log); otherwise fall back to the compiler estimate.
    const override =
      avgRepMap && item.node?.id ? avgRepMap[item.node.id] : null;
    total += Number.isFinite(override) && override > 0 ? override : item.durationSec;
  }
  return total;
};

// Single source of truth for display durations. Always renders as M:SS so
// every row, header, and tooltip on the plan view speaks the same dialect.
// Returns null for non-positive durations so callers can branch on it cheaply.
export const formatDisplayDuration = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null;
  const total = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const paddedSeconds = seconds < 10 ? `0${seconds}` : String(seconds);
  return `${minutes}:${paddedSeconds}`;
};

// Back-compat wrappers. New code should call formatDisplayDuration directly;
// callers updated to use it can drop these imports over time.
export const formatDuration = (totalSeconds) =>
  formatDisplayDuration(totalSeconds);

export const formatDurationCompact = (totalSeconds) =>
  formatDisplayDuration(totalSeconds);

// ─── Timestamps ───────────────────────────────────────────
//
// Not a duration: how long ago a thing happened, for feed cards, notifications
// and anything else stamped with a wall-clock time. Falls back to a plain date
// past a week, because "23d ago" is harder to place than the date itself.
export const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};
